<script module lang="ts">
  type KeepScrolling = () => Promise<boolean>
</script>

<script lang="ts">
  import { assertBoolean } from '#app/lib/assert_types'
  import viewport from '#app/lib/components/actions/viewport'
  import { wait } from '#app/lib/promises'
  import Spinner from '#components/spinner.svelte'
  import type { Snippet } from 'svelte'

  const {
    keepScrolling,
    showSpinner = false,
    children,
    bottomOffsetTop,
  }: {
    keepScrolling: KeepScrolling
    showSpinner?: boolean
    children?: Snippet
    bottomOffsetTop?: number
  } = $props()

  let earlyBottomElInView = $state(false)
  let bottomElInView = $state(false)
  let fetching = $state(false)
  let loadingMore: ReturnType<KeepScrolling> = $state()

  async function fetchMore () {
    if (fetching) return
    fetching = true
    loadingMore = keepScrolling()
    // Error handling should be done within keepScrolling
    const canLoadMore = await loadingMore
    fetching = false
    assertBoolean(canLoadMore)
    if (canLoadMore) {
      // Let the time to fetched content to render
      await wait(500)
      // But if the bottom is still in viewport
      // when the new content rendered, fetch more
      if (earlyBottomElInView || bottomElInView) fetchMore()
    }
  }

  async function earlyBottomIsInViewport () {
    earlyBottomElInView = true
    await fetchMore()
  }

  function earlyBottomLeftViewport () {
    earlyBottomElInView = false
  }

  async function bottomIsInViewport () {
    bottomElInView = true
    await fetchMore()
  }

  function bottomLeftViewport () {
    bottomElInView = false
  }

  let infiniteScrollWrapperRect: DOMRect = $state()
</script>

<div class="infinite-scroll-wrapper" bind:contentRect={infiniteScrollWrapperRect}>
  {@render children?.()}
  {#if bottomOffsetTop}
    <div
      class="early-bottom"
      use:viewport
      style:top={`${bottomOffsetTop - 500}px`}
      onenterViewport={earlyBottomIsInViewport}
      onleaveViewport={earlyBottomLeftViewport}
    ></div>
  {/if}
  <div
    class="bottom"
    use:viewport
    onenterViewport={bottomIsInViewport}
    onleaveViewport={bottomLeftViewport}
  ></div>
  {#if showSpinner}
    {#await loadingMore}
      <Spinner center={true} />
    {/await}
  {/if}
</div>

<style lang="scss">
  @import "general/scss/utils";
  .infinite-scroll-wrapper{
    position: relative;
    // Required to let children with position=sticky choose their horizontal alignment
    // Ex: .pagination-state
    @include display-flex(column, stretch, flex-start);
  }
  .bottom, .early-bottom{
    position: absolute;
    inset-inline: 0;
    z-index: 1;
    min-height: 1px;
  }
  .bottom{
    inset-block-end: min(50%, 80vh);
  }
</style>
